/**
 * Call Detail Modal - Read-Only View
 *
 * Shows comprehensive call log information when clicked from customer view
 */

import { getLocaleSync } from '@/utils/locale';
import { X, Phone, Clock, User, FileText, AlertCircle, Calendar, Briefcase } from 'lucide-react';
import { CallLog } from '../types/schema';
import { PhoneNumber } from './PhoneNumber';

interface CallDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  call: CallLog | null;
}

export function CallDetailModal({ isOpen, onClose, call }: CallDetailModalProps) {
  if (!isOpen || !call) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString(getLocaleSync(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getCallTypeColor = (type: string) => {
    const colors = {
      sales: 'bg-green-500/20 text-green-400 border-green-500/30',
      fault: 'bg-red-500/20 text-red-400 border-red-500/30',
      enquiry: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      billing: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    };
    return colors[type as keyof typeof colors] || 'bg-white/10 text-white/60 border-white/15';
  };

  const getUrgencyColor = (urgency: string) => {
    const colors = {
      low: 'bg-blue-500/20 text-blue-400',
      normal: 'bg-slate-500/20 text-slate-400',
      high: 'bg-orange-500/20 text-orange-400',
      urgent: 'bg-red-500/20 text-red-400',
    };
    return colors[urgency as keyof typeof colors] || 'bg-gray-500/20 text-gray-400';
  };

  const getStatusColor = (status: string) => {
    const colors = {
      logged: 'bg-blue-500/20 text-blue-400',
      converted_to_deal: 'bg-green-500/20 text-green-400',
      no_action_needed: 'bg-gray-500/20 text-gray-400',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[250] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gradient-to-br from-brand-primary to-brand-dark border border-brand-secondary/30 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand-secondary/20 to-transparent backdrop-blur-sm border-b border-white/10 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-secondary to-yellow-600 flex items-center justify-center shadow-lg">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Call Details</h2>
              <p className="text-sm text-slate-400">{call.customerName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Call Type & Status */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getCallTypeColor(call.callType)}`}>
              {call.callType.charAt(0).toUpperCase() + call.callType.slice(1)}
            </span>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(call.status)}`}>
              {call.status === 'logged' ? 'Logged' :
               call.status === 'converted_to_deal' ? 'Converted to Deal' :
               'No Action Needed'}
            </span>
            {call.urgency && call.urgency !== 'normal' && (
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getUrgencyColor(call.urgency)}`}>
                {call.urgency.charAt(0).toUpperCase() + call.urgency.slice(1)} Urgency
              </span>
            )}
          </div>

          {/* Customer Contact Info */}
          <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-3">
            <h3 className="text-lg font-bold text-white mb-3">Customer Information</h3>

            <div className="flex items-center gap-3 text-slate-300">
              <User className="w-5 h-5 text-brand-secondary" />
              <span className="font-semibold">{call.customerName}</span>
            </div>

            {call.customerPhone && (
              <div className="flex items-center gap-3 text-slate-300">
                <Phone className="w-5 h-5 text-brand-secondary" />
                <PhoneNumber phone={call.customerPhone} />
              </div>
            )}

            {call.customerEmail && (
              <div className="flex items-center gap-3 text-slate-300">
                <FileText className="w-5 h-5 text-brand-secondary" />
                <span>{call.customerEmail}</span>
              </div>
            )}
          </div>

          {/* Services (for sales calls) */}
          {call.services && call.services.length > 0 && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4">
              <h3 className="text-lg font-bold text-white mb-3">Services Discussed</h3>
              <div className="flex flex-wrap gap-2">
                {call.services.map((service, idx) => (
                  <span key={idx} className="px-3 py-1 bg-brand-secondary/20 text-brand-secondary rounded-full text-sm font-semibold">
                    {service}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Call Notes */}
          <div className="bg-white/5 rounded-lg border border-white/10 p-4">
            <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-brand-secondary" />
              Call Notes
            </h3>
            <p className="text-slate-300 whitespace-pre-wrap">{call.notes}</p>
          </div>

          {/* Follow-up */}
          {call.requiresFollowUp && (
            <div className="bg-yellow-500/10 rounded-lg border border-yellow-500/30 p-4">
              <h3 className="text-lg font-bold text-yellow-400 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Follow-up Required
              </h3>
              <div className="space-y-2 text-slate-300">
                {call.followUpDate && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-yellow-400" />
                    <span className="font-semibold">Date:</span> {call.followUpDate}
                  </div>
                )}
                {call.followUpNotes && (
                  <p className="text-sm">{call.followUpNotes}</p>
                )}
              </div>
            </div>
          )}

          {/* Conversion Info */}
          {call.status === 'converted_to_deal' && call.convertedToDealId && (
            <div className="bg-green-500/10 rounded-lg border border-green-500/30 p-4">
              <h3 className="text-lg font-bold text-green-400 mb-3 flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Converted to Deal
              </h3>
              <div className="space-y-2 text-slate-300 text-sm">
                <div>
                  <span className="text-slate-500">Deal ID:</span> {call.convertedToDealId}
                </div>
                {call.convertedAt && (
                  <div>
                    <span className="text-slate-500">Converted:</span> {formatDate(call.convertedAt)}
                  </div>
                )}
                {call.convertedBy && (
                  <div>
                    <span className="text-slate-500">By:</span> {call.convertedBy}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Call Metadata */}
          <div className="bg-white/5 rounded-lg border border-white/10 p-4">
            <h3 className="text-lg font-bold text-white mb-3">Call Information</h3>
            <div className="space-y-2 text-sm text-slate-300">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-brand-secondary" />
                <span className="text-slate-500">Logged:</span> {formatDate(call.timestamp)}
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-brand-secondary" />
                <span className="text-slate-500">Logged by:</span> {call.loggedByName}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 p-4 bg-white/5">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
